import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface GeneratePromptInput {
  intent: string;
  useCase: string;
  model?: string;
  outputType?: string;
  difficulty?: string;
  category?: string;
}

export interface GeneratedPrompt {
  title: string;
  description: string;
  content: string;
  keywords: string[];
}

export function useGeneratePrompt() {
  const [isGenerating, setIsGenerating] = useState(false);

  const generate = async (input: GeneratePromptInput): Promise<GeneratedPrompt | null> => {
    if (!input.intent || !input.useCase) {
      toast.error("Please select Intent and Use Case before generating.");
      return null;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-prompt", {
        body: input,
      });

      if (error) {
        const msg = error.message || "Failed to generate prompt";
        if (msg.includes("429") || msg.includes("rate limit")) {
          toast.error("Rate limit exceeded. Please wait a moment and try again.");
        } else if (msg.includes("402") || msg.includes("credits")) {
          toast.error("AI credits exhausted. Please add credits to your workspace.");
        } else {
          toast.error(msg);
        }
        return null;
      }

      if (data?.error) {
        toast.error(data.error);
        return null;
      }

      return data as GeneratedPrompt;
    } catch (err) {
      console.error("Generate prompt error:", err);
      toast.error("Something went wrong. Please try again.");
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  return { generate, isGenerating };
}
